import { IFlatConfigFormat, INestedConfigFormat } from './configEditorTypes';

export function convertFlatToNestedFormat(flatConfig: IFlatConfigFormat): INestedConfigFormat {
  // Initialize the nested structure
  const nestedConfig: INestedConfigFormat = {
    databases: [],
  };

  // Helper function to extract name from reference
  const extractNameFromRef = (ref: string, type: string): string => {
    const pattern = new RegExp(`rel\\(${type}\\.(.*?)\\)`);
    const match = ref.match(pattern);
    return match !== null ? match[1] : ref;
  };

  // Maps to track objects by their names
  const databaseMap = new Map<string, number>();
  const schemaMap = new Map<string, { dbIndex: number; schemaIndex: number }>();
  const tableMap = new Map<string, { dbIndex: number; schemaIndex: number; tableIndex: number }>();

  // First pass: Extract all databases
  flatConfig.forEach((item) => {
    // Check if the item has a database property
    if (item.database !== undefined) {
      const dbName = item.database.name;

      if (!databaseMap.has(dbName)) {
        databaseMap.set(dbName, nestedConfig.databases.length);
        nestedConfig.databases.push({
          ...item.database,
          schemas: [],
        });
      }
    }
  });

  // Second pass: Extract all schemas and associate them with their databases
  flatConfig.forEach((item) => {
    // Check if the item has a schema property
    if (item.schema !== undefined) {
      const schemaName = item.schema.name;
      const dbRef = item.schema.database;

      // Extract database name from reference
      const dbName = extractNameFromRef(dbRef, 'database');

      const dbIndex = databaseMap.get(dbName);

      if (dbIndex !== undefined) {
        const schemaKey = `${dbName}.${schemaName}`;

        if (!schemaMap.has(schemaKey)) {
          schemaMap.set(schemaKey, {
            dbIndex,
            schemaIndex: nestedConfig.databases[dbIndex].schemas.length,
          });

          nestedConfig.databases[dbIndex].schemas.push({
            ...item.schema,
            database: dbName, // Normalize the reference
            tables: [],
            views: [],
            procedures: [],
            functions: [],
            file_formats: [],
            sequences: [],
          });
        }
      }
    }
  });

  // Third pass: Extract all tables and views and associate them with their schemas
  flatConfig.forEach((item) => {
    // Handle tables
    if (item.table !== undefined) {
      const tableName = item.table.name;
      const schemaRef = item.table.schema;
      const dbRef = item.table.database;

      // Extract schema and database names from references
      const schemaName = extractNameFromRef(schemaRef, 'schema');
      const dbName = extractNameFromRef(dbRef, 'database');

      const schemaKey = `${dbName}.${schemaName}`;
      const schemaInfo = schemaMap.get(schemaKey);

      if (schemaInfo !== undefined) {
        const { dbIndex, schemaIndex } = schemaInfo;
        const tableKey = `${dbName}.${schemaName}.${tableName}`;

        if (!tableMap.has(tableKey)) {
          tableMap.set(tableKey, {
            dbIndex,
            schemaIndex,
            tableIndex: nestedConfig.databases[dbIndex].schemas[schemaIndex].tables.length,
          });

          // Convert columns from object to array if needed
          let columns = [];
          if (item.table.columns !== undefined) {
            if (Array.isArray(item.table.columns)) {
              columns = item.table.columns;
            } else {
              // Convert object-based columns to array format
              columns = Object.entries(item.table.columns).map(([name, config]: [string, any]) => ({
                name,
                ...config,
                database: dbName,
                schema: schemaName,
                table: tableName,
              }));
            }
          }

          nestedConfig.databases[dbIndex].schemas[schemaIndex].tables.push({
            ...item.table,
            database: dbName, // Normalize the reference
            schema: schemaName, // Normalize the reference
            columns,
          });
        }
      }
    }
    // Handle views
    else if (item.view !== undefined) {
      const schemaRef = item.view.schema;
      const dbRef = item.view.database;

      // Extract schema and database names from references
      const schemaName = extractNameFromRef(schemaRef, 'schema');
      const dbName = extractNameFromRef(dbRef, 'database');

      const schemaKey = `${dbName}.${schemaName}`;
      const schemaInfo = schemaMap.get(schemaKey);

      if (schemaInfo !== undefined) {
        const { dbIndex, schemaIndex } = schemaInfo;

        nestedConfig.databases[dbIndex].schemas[schemaIndex].views.push({
          ...item.view,
          database: dbName, // Normalize the reference
          schema: schemaName, // Normalize the reference
        });
      }
    }
    // Handle procedures
    else if (item.procedure !== undefined) {
      const schemaRef = item.procedure.schema;
      const dbRef = item.procedure.database;

      // Extract schema and database names from references
      const schemaName = extractNameFromRef(schemaRef, 'schema');
      const dbName = extractNameFromRef(dbRef, 'database');

      const schemaKey = `${dbName}.${schemaName}`;
      const schemaInfo = schemaMap.get(schemaKey);

      if (schemaInfo !== undefined) {
        const { dbIndex, schemaIndex } = schemaInfo;

        nestedConfig.databases[dbIndex].schemas[schemaIndex].procedures.push({
          ...item.procedure,
          database: dbName, // Normalize the reference
          schema: schemaName, // Normalize the reference
        });
      }
    }
    // Handle functions
    else if (item.function !== undefined) {
      const schemaRef = item.function.schema;
      const dbRef = item.function.database;

      // Extract schema and database names from references
      const schemaName = extractNameFromRef(schemaRef, 'schema');
      const dbName = extractNameFromRef(dbRef, 'database');

      const schemaKey = `${dbName}.${schemaName}`;
      const schemaInfo = schemaMap.get(schemaKey);

      if (schemaInfo !== undefined) {
        const { dbIndex, schemaIndex } = schemaInfo;

        nestedConfig.databases[dbIndex].schemas[schemaIndex].functions.push({
          ...item.function,
          database: dbName, // Normalize the reference
          schema: schemaName, // Normalize the reference
        });
      }
    }
    // Handle file formats
    else if (item.file_format !== undefined) {
      const schemaRef = item.file_format.schema;
      const dbRef = item.file_format.database;

      // Extract schema and database names from references
      const schemaName = extractNameFromRef(schemaRef, 'schema');
      const dbName = extractNameFromRef(dbRef, 'database');

      const schemaKey = `${dbName}.${schemaName}`;
      const schemaInfo = schemaMap.get(schemaKey);

      if (schemaInfo !== undefined) {
        const { dbIndex, schemaIndex } = schemaInfo;

        nestedConfig.databases[dbIndex].schemas[schemaIndex].file_formats.push({
          ...item.file_format,
          database: dbName, // Normalize the reference
          schema: schemaName, // Normalize the reference
        });
      }
    }
    // Handle sequences
    else if (item.sequence !== undefined) {
      const schemaRef = item.sequence.schema;
      const dbRef = item.sequence.database;

      // Extract schema and database names from references
      const schemaName = extractNameFromRef(schemaRef, 'schema');
      const dbName = extractNameFromRef(dbRef, 'database');

      const schemaKey = `${dbName}.${schemaName}`;
      const schemaInfo = schemaMap.get(schemaKey);

      if (schemaInfo !== undefined) {
        const { dbIndex, schemaIndex } = schemaInfo;

        nestedConfig.databases[dbIndex].schemas[schemaIndex].sequences.push({
          ...item.sequence,
          database: dbName, // Normalize the reference
          schema: schemaName, // Normalize the reference
        });
      }
    }
  });

  return nestedConfig;
}

export function convertNestedToFlatFormat(nestedConfig: INestedConfigFormat): IFlatConfigFormat {
  const flatConfig: IFlatConfigFormat = [];

  nestedConfig.databases.forEach((database) => {
    // Create a new database object without the schemas property
    const dbConfig = { ...database };
    // @ts-expect-error - Remove schemas property which is not part of IDatabaseConfig
    delete dbConfig.schemas;

    // Add database to flatConfig
    flatConfig.push({ database: dbConfig });

    database.schemas.forEach((schema) => {
      // Create a new schema object without the tables and views properties
      const schemaConfig = { ...schema };
      // @ts-expect-error - Remove properties which are not part of ISchemaConfig
      delete schemaConfig.tables;
      // @ts-expect-error - Remove properties which are not part of ISchemaConfig
      delete schemaConfig.views;
      // @ts-expect-error - Remove properties which are not part of ISchemaConfig
      delete schemaConfig.procedures;
      // @ts-expect-error - Remove properties which are not part of ISchemaConfig
      delete schemaConfig.functions;
      // @ts-expect-error - Remove properties which are not part of ISchemaConfig
      delete schemaConfig.file_formats;
      // @ts-expect-error - Remove properties which are not part of ISchemaConfig
      delete schemaConfig.sequences;

      // Set the database reference
      schemaConfig.database = `rel(database.${database.name})`;

      // Add schema to flatConfig
      flatConfig.push({ schema: schemaConfig });

      schema.tables.forEach((table) => {
        // Create a new table object with references to database and schema
        const tableConfig = { ...table };
        tableConfig.database = `rel(database.${database.name})`;
        tableConfig.schema = `rel(schema.${schema.name})`;

        // Convert columns array back to object format if needed
        if (Array.isArray(table.columns)) {
          const columnsObj: Record<string, any> = {};
          table.columns.forEach((column) => {
            // Remove database, schema, and table properties from column
            const { name, database, schema, table: tableName, ...columnProps } = column;
            columnsObj[name] = columnProps;
          });
          // Use type assertion to handle the type mismatch
          (tableConfig as any).columns = columnsObj;
        }

        // Add table to flatConfig
        flatConfig.push({ table: tableConfig });
      });

      schema.views.forEach((view) => {
        // Create a new view object with references to database and schema
        const viewConfig = { ...view };
        viewConfig.database = `rel(database.${database.name})`;
        viewConfig.schema = `rel(schema.${schema.name})`;

        // Add view to flatConfig
        flatConfig.push({ view: viewConfig });
      });

      // Handle procedures
      schema.procedures.forEach((procedure) => {
        // Create a new procedure object with references to database and schema
        const procedureConfig = { ...procedure };
        procedureConfig.database = `rel(database.${database.name})`;
        procedureConfig.schema = `rel(schema.${schema.name})`;

        // Add procedure to flatConfig
        flatConfig.push({ procedure: procedureConfig });
      });

      // Handle functions
      schema.functions.forEach((func) => {
        // Create a new function object with references to database and schema
        const functionConfig = { ...func };
        functionConfig.database = `rel(database.${database.name})`;
        functionConfig.schema = `rel(schema.${schema.name})`;

        // Add function to flatConfig
        flatConfig.push({ function: functionConfig });
      });

      // Handle file formats
      schema.file_formats.forEach((format) => {
        // Create a new file format object with references to database and schema
        const formatConfig = { ...format };
        formatConfig.database = `rel(database.${database.name})`;
        formatConfig.schema = `rel(schema.${schema.name})`;

        // Add file format to flatConfig
        flatConfig.push({ file_format: formatConfig });
      });

      // Handle sequences
      schema.sequences.forEach((sequence) => {
        // Create a new sequence object with references to database and schema
        const sequenceConfig = { ...sequence };
        sequenceConfig.database = `rel(database.${database.name})`;
        sequenceConfig.schema = `rel(schema.${schema.name})`;

        // Add sequence to flatConfig
        flatConfig.push({ sequence: sequenceConfig });
      });
    });
  });

  return flatConfig;
}
